// src/pages/forumcomponents/PostList.js — V18 post rows.
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../api/apiClient';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import defaultavatar from '../../assets/default_avatar.png';
import { Tag, GhostButton, Muted } from '../../components/ui';
import { color, font } from '../../theme/tokens';

const Rows = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Row = styled.button`
    all: unset;
    box-sizing: border-box;
    width: 100%;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 24px 28px;
    background-color: ${color.surface};
    border: 1px solid ${color.line};
    color: ${color.text};
    transition: background-color 0.3s ease, border-color 0.3s ease;
    &:hover { background-color: ${color.surface2}; border-color: rgba(200, 50, 62, 0.45); }
    &:focus-visible { outline: 2px solid ${color.bloodHi}; outline-offset: 2px; }
    @media (max-width: 720px) { gap: 14px; padding: 16px; }
`;

const Avatar = styled.img`
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    box-sizing: border-box;
    object-fit: cover;
    border: 1px solid ${color.blood};
    background: ${color.void};
    @media (max-width: 720px) { width: 40px; height: 40px; }
`;

const Main = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex-grow: 1;
    min-width: 0;
    .title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-family: ${font.display};
        font-weight: 800;
        font-size: 20px;
        letter-spacing: 0.08em;
        overflow: hidden;
    }
    .title span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .meta { display: flex; align-items: center; gap: 10px; font-size: 13px; color: ${color.text3}; flex-wrap: wrap; }
    .meta b { font-weight: 400; color: ${color.text2}; }
    @media (max-width: 720px) { .title { font-size: 16px; } .meta { font-size: 12px; } }
`;

const Stats = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    min-width: 72px;
    .n { font-family: ${font.mono}; font-size: 22px; color: ${color.text}; }
    .l { font-size: 12px; color: ${color.text3}; letter-spacing: 0.2em; }
    @media (max-width: 720px) { display: none; }
`;

const Pager = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-top: 32px;
    span { font-family: ${font.mono}; font-size: 13px; color: ${color.text3}; }
`;

const ago = (d) => formatDistanceToNow(new Date(d), { addSuffix: true, locale: zhCN });

const PostList = ({ onPostSelect, refreshTrigger }) => {
    const [data, setData] = useState({ posts: [], totalPages: 1, currentPage: 1 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchPosts = async (page = 1) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await apiClient.get(`/public/forum/posts?page=${page}&limit=15`);
            setData(response.data);
        } catch (err) {
            console.error('Failed to fetch posts:', err);
            setError('帖子载入失败，请稍后再试。');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts(1);
    }, [refreshTrigger]);

    const go = (p) => { if (p > 0 && p <= data.totalPages) fetchPosts(p); };

    if (isLoading) return <Muted>载入中…</Muted>;
    if (error) return <Muted style={{ color: color.bloodHi }}>{error}</Muted>;
    if (data.posts.length === 0) return <Muted>还没有帖子。</Muted>;

    return (
        <>
            <Rows>
                {data.posts.map((post) => (
                    <li key={post.id}>
                        <Row type="button" onClick={() => onPostSelect(post.id)}>
                            <Avatar src={post.author_avatar_url || defaultavatar} alt="" />
                            <Main>
                                <div className="title">
                                    {post.is_official && <Tag $solid>官方</Tag>}
                                    <span>{post.title}</span>
                                </div>
                                <div className="meta">
                                    <b>{post.author_nickname || '匿名'}</b>
                                    <span>·</span>
                                    <span>{ago(post.created_at)}</span>
                                    <span>·</span>
                                    <span title={new Date(post.last_reply_at).toLocaleString()}>最后回复 {ago(post.last_reply_at)}</span>
                                </div>
                            </Main>
                            <Stats>
                                <span className="n">{post.reply_count}</span>
                                <span className="l">回复</span>
                            </Stats>
                        </Row>
                    </li>
                ))}
            </Rows>
            {data.totalPages > 1 && (
                <Pager>
                    <GhostButton type="button" onClick={() => go(data.currentPage - 1)} disabled={data.currentPage === 1}>上一页</GhostButton>
                    <span>{data.currentPage} / {data.totalPages}</span>
                    <GhostButton type="button" onClick={() => go(data.currentPage + 1)} disabled={data.currentPage === data.totalPages}>下一页</GhostButton>
                </Pager>
            )}
        </>
    );
};

export default PostList;
